import {
  type DuplicateCloneCategory,
  type EnterpriseSummary,
  type ResolvedDuplicateCloneOptions,
  DUPLICATE_CLONE_CATEGORY_KEYS,
  resolveDuplicateCloneOptions,
  selectiveDuplicateCloneDefaults,
} from "@mg/schemas/src/christo/catalyst";
import { EnterpriseProfileType } from "@mg/schemas/src/commons";
import { Copy } from "@phosphor-icons/react";
import {
  Dialog,
  Button,
  TextField,
  Flex,
  Text,
  Checkbox,
} from "@radix-ui/themes";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUI } from "../contexts/ui";
import { useAnalytics } from "../utils/analytics";
import {
  enterpriseNameToSlug,
  enterpriseSlugProblem,
  findEnterpriseIdentityConflict,
  suggestDuplicateEnterpriseName,
} from "../utils/enterpriseDuplicate";
import { useAppSelector } from "../utils/hooks";
import { errorMessage } from "../utils/http";
import { stashPendingNotification } from "../utils/pendingNotifications";
import {
  useDuplicateEnterprise,
  useEnterprises,
  useSwitchEnterprise,
} from "../utils/queries/enterprises";
import { BaseDialogProps } from "../utils/types/dialog";

export type DuplicateEnterpriseDialogProps = BaseDialogProps & {
  sourceEnterprise: EnterpriseSummary | null;
};

const CATEGORY_LABELS: Record<DuplicateCloneCategory, string> = {
  settings: "Settings, skills & personas",
  integrations: "Integrations",
  folders: "Folders",
  tickets: "Tickets & revision boards",
  aicache: "AI review cache",
  askPuntt: "Ask Puntt chats & projects",
  aiContent: "AI guidelines & memories",
  knowledge: "Knowledge database",
  assets: "Assets & files (S3)",
  userMemberships: "User memberships",
  teams: "Teams",
  notes: "Notes",
};

function emptyCloneState(): ResolvedDuplicateCloneOptions {
  return selectiveDuplicateCloneDefaults({});
}

export function DuplicateEnterpriseDialog({
  open,
  onOpenChange,
  sourceEnterprise,
}: DuplicateEnterpriseDialogProps) {
  const { t } = useTranslation("ui");
  const setOpen = onOpenChange;
  const [enterpriseName, setEnterpriseName] = useState("");
  const [enterpriseSlug, setEnterpriseSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [isDemo, setIsDemo] = useState(true);
  const [fullClone, setFullClone] = useState(false);
  const [cloneOptions, setCloneOptions] =
    useState<ResolvedDuplicateCloneOptions>(emptyCloneState);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);

  const duplicateMutation = useDuplicateEnterprise();
  const enterprisesQuery = useEnterprises();
  const switchEnterpriseMutation = useSwitchEnterprise();
  const { notify } = useUI();
  const posthog = useAnalytics("DuplicateEnterpriseDialog");
  const user = useAppSelector((state) => state.auth.value);

  const effectiveOptions = useMemo(
    () =>
      resolveDuplicateCloneOptions({
        fullClone,
        clone: cloneOptions,
      }),
    [fullClone, cloneOptions],
  );

  useEffect(() => {
    if (!open || !sourceEnterprise) return;
    const suggestedName = suggestDuplicateEnterpriseName(sourceEnterprise.name);
    setEnterpriseName(suggestedName);
    setEnterpriseSlug(enterpriseNameToSlug(suggestedName));
    setSlugTouched(false);
    setIsDemo(true);
    setFullClone(false);
    setCloneOptions(emptyCloneState());
    setStatusMessage(null);
  }, [open, sourceEnterprise]);

  useEffect(() => {
    if (slugTouched) return;
    setEnterpriseSlug(enterpriseNameToSlug(enterpriseName));
  }, [enterpriseName, slugTouched]);

  useEffect(() => {
    if (open) return;
    pollAbortRef.current?.abort();
  }, [open]);

  useEffect(() => () => pollAbortRef.current?.abort(), []);

  if (user?.role !== EnterpriseProfileType.MEANINGFUL_GIGS) {
    return null;
  }

  const setCategory = (key: DuplicateCloneCategory, value: boolean) => {
    setFullClone(false);
    setCloneOptions((prev) => ({ ...prev, [key]: value }));
  };

  const trimmedName = enterpriseName.trim();
  const trimmedSlug = enterpriseSlug.trim();
  const identityConflict = findEnterpriseIdentityConflict(
    enterprisesQuery.data ?? [],
    { name: trimmedName, slug: trimmedSlug },
  );
  const slugProblem = enterpriseSlugProblem(trimmedSlug);
  const nameError =
    identityConflict === "name"
      ? t("createEnterpriseDialog.nameTaken", { name: trimmedName })
      : null;
  const slugError =
    identityConflict === "slug"
      ? t("createEnterpriseDialog.slugTaken", { slug: trimmedSlug })
      : slugProblem === "length"
        ? t("settings.enterpriseSettingsView.slugMaxLength")
        : slugProblem === "charset"
          ? t(
              "settings.enterpriseSettingsView.useLowercaseLettersNumbersHyphens",
            )
          : null;

  const handleSave = async () => {
    if (!sourceEnterprise || nameError || slugError) return;

    if (!trimmedName || !trimmedSlug) return;

    pollAbortRef.current?.abort();
    const controller = new AbortController();
    pollAbortRef.current = controller;

    try {
      setStatusMessage("Starting duplicate…");
      const result = await duplicateMutation.mutateAsync({
        sourceEnterpriseId: sourceEnterprise._id,
        name: trimmedName,
        slug: trimmedSlug,
        isDemo,
        fullClone,
        clone: fullClone ? undefined : cloneOptions,
        signal: controller.signal,
      });

      // The dialog was closed (or a newer submit took over) while the job
      // was still polling, so this controller was aborted. The user asked
      // us to let go: do not stash a toast, switch tenants, or reload them
      // into an enterprise they walked away from.
      if (controller.signal.aborted) return;

      const targetId = result.targetEnterpriseId;
      if (!targetId) {
        throw new Error("Duplicate finished without a target enterprise id");
      }

      posthog.capture("duplicated_enterprise", {
        sourceEnterpriseId: sourceEnterprise._id,
        sourceName: sourceEnterprise.name,
        targetEnterpriseId: targetId,
        isDemo,
        fullClone,
      });

      stashPendingNotification({
        variant: "success",
        title: "Enterprise duplicated",
        message: `Created ${trimmedName} from ${sourceEnterprise.name}.`,
      });

      setOpen?.(false);
      setEnterpriseName("");
      setEnterpriseSlug("");
      setSlugTouched(false);
      setIsDemo(true);
      setFullClone(false);
      setCloneOptions(emptyCloneState());
      setStatusMessage(null);

      switchEnterpriseMutation.mutate(targetId, {
        onSuccess: () => {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Failed to duplicate enterprise:", error);
      setStatusMessage(null);
      const message = (await errorMessage(error)).trim();
      notify({
        variant: "error",
        title: "Duplicate failed",
        message: message || "Could not duplicate this enterprise. Try again.",
      });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Content
        data-testid="duplicate-enterprise-dialog"
        aria-labelledby="duplicate-enterprise-dialog-title"
      >
        <Dialog.Title id="duplicate-enterprise-dialog-title">
          <Flex align="center" gap="2">
            <Copy aria-hidden="true" />
            Duplicate enterprise
          </Flex>
        </Dialog.Title>
        <Dialog.Description size="2" mb="3">
          Create a new enterprise from{" "}
          <strong>{sourceEnterprise?.name ?? "the source"}</strong>. Choose a
          full clone or pick categories.
        </Dialog.Description>

        <form
          data-testid="duplicate-enterprise-form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <Flex direction="column" gap="3">
            <label htmlFor="duplicate-enterprise-name">
              <Text size="2" weight="medium">
                Name
              </Text>
              <TextField.Root
                id="duplicate-enterprise-name"
                name="duplicate-enterprise-name"
                value={enterpriseName}
                onChange={(e) => setEnterpriseName(e.target.value)}
                data-testid="duplicate-enterprise-name-input"
                aria-invalid={nameError ? true : undefined}
                aria-describedby={
                  nameError ? "duplicate-enterprise-name-error" : undefined
                }
                mt="1"
              />
            </label>
            <label htmlFor="duplicate-enterprise-slug">
              <Text size="2" weight="medium">
                Slug
              </Text>
              <TextField.Root
                id="duplicate-enterprise-slug"
                name="duplicate-enterprise-slug"
                value={enterpriseSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setEnterpriseSlug(e.target.value);
                }}
                data-testid="duplicate-enterprise-slug-input"
                aria-invalid={slugError ? true : undefined}
                aria-describedby={
                  slugError ? "duplicate-enterprise-slug-error" : undefined
                }
                mt="1"
              />
            </label>
            {nameError ? (
              <Text
                id="duplicate-enterprise-name-error"
                data-testid="duplicate-enterprise-name-error"
                size="1"
                color="red"
              >
                {nameError}
              </Text>
            ) : null}
            {slugError ? (
              <Text
                id="duplicate-enterprise-slug-error"
                data-testid="duplicate-enterprise-slug-error"
                size="1"
                color="red"
              >
                {slugError}
              </Text>
            ) : null}

            <Text
              as="label"
              size="2"
              htmlFor="duplicate-enterprise-demo-checkbox"
            >
              <Flex gap="2" align="center">
                <Checkbox
                  id="duplicate-enterprise-demo-checkbox"
                  checked={isDemo}
                  onCheckedChange={(v) => setIsDemo(v === true)}
                  data-testid="duplicate-enterprise-demo-checkbox"
                />
                Mark as demo enterprise
              </Flex>
            </Text>

            <Text
              as="label"
              size="2"
              htmlFor="duplicate-enterprise-full-clone-checkbox"
            >
              <Flex gap="2" align="center">
                <Checkbox
                  id="duplicate-enterprise-full-clone-checkbox"
                  checked={fullClone}
                  onCheckedChange={(v) => setFullClone(v === true)}
                  data-testid="duplicate-enterprise-full-clone-checkbox"
                  aria-label="Full clone everything including users"
                />
                Full clone (100% — users, cache, teams, notes, everything)
              </Flex>
            </Text>

            {!fullClone ? (
              <fieldset
                data-testid="duplicate-enterprise-clone-options"
                aria-label="Clone categories"
                className="m-0 border-0 p-0"
              >
                <Text size="2" weight="medium" mb="2">
                  What to clone
                </Text>
                <Flex direction="column" gap="2">
                  {DUPLICATE_CLONE_CATEGORY_KEYS.map((key) => (
                    <Text
                      as="label"
                      size="2"
                      key={key}
                      htmlFor={`duplicate-enterprise-clone-${key}`}
                    >
                      <Flex gap="2" align="center">
                        <Checkbox
                          id={`duplicate-enterprise-clone-${key}`}
                          checked={effectiveOptions[key]}
                          onCheckedChange={(v) => setCategory(key, v === true)}
                          data-testid={`duplicate-enterprise-clone-${key}`}
                          aria-label={CATEGORY_LABELS[key]}
                        />
                        {CATEGORY_LABELS[key]}
                      </Flex>
                    </Text>
                  ))}
                </Flex>
              </fieldset>
            ) : null}

            {statusMessage ? (
              <Text
                size="2"
                color="gray"
                data-testid="duplicate-enterprise-status"
              >
                {statusMessage}
              </Text>
            ) : null}

            <Flex gap="3" mt="2" justify="end">
              <Dialog.Close>
                <Button
                  type="button"
                  variant="soft"
                  color="gray"
                  data-testid="duplicate-enterprise-cancel-button"
                  aria-label="Cancel duplicate enterprise"
                >
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                type="submit"
                disabled={
                  !enterpriseName.trim() ||
                  !enterpriseSlug.trim() ||
                  nameError != null ||
                  slugError != null ||
                  duplicateMutation.isPending
                }
                loading={duplicateMutation.isPending}
                data-testid="duplicate-enterprise-submit-button"
                aria-label="Duplicate enterprise"
              >
                Duplicate
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
